// 配車・受注時の自社便 試算（確定実績ではない）。
// 人件費（2026-08-28 所要時間ベースへ変更・オーナー料率指定）:
//   所要時間（Google経路・分）があれば 拘束時間×時給（中型2,200/大型2,400円/h・8h超×1.25・
//   日祝着+20%・深夜積卸+20%。式と重ね方は laborQuote.core.js に集約）。
//   無ければ従来どおり 運賃×「試算用」率（人件費設定・初期値35%）へフォールバック。
// 車両日額 大型2万/中型1.1万、営業日は土日祝除外。
// 燃料試算（2026-08-28 車格別へ変更・オーナー指定）＝距離 ÷ **車格別**燃費（中型4km/L・大型2km/L）× 軽油単価。
//   設定はダッシュボードの前提（試算）パネル＝settings/assumptions。既定・正規化は quoteFuel.core.js に集約。
//   ⚠️ 実績の燃費（同パネルの kmL＝デジタコ実測の上書き）とは**別物**。役割の境界は quoteFuel.core.js 冒頭に明記。
// ※実績の収支（運行実績×収支・乗務員別・KPI）はここを通らない＝一切変わらない。
import { LABOR_RATE, VEHICLE_DAY_RATE, isBusinessDay, japanHolidays, getLabor } from './analytics/operations.js'
import { timeLaborCost, isNightTime } from './laborQuote.core.js'
import { fuelRateOf } from './quoteFuel.core.js'

export const QUOTE_MARGIN = 0.15 // 目安運賃＝運行経費×15%

const pad2 = (n) => String(n).padStart(2, '0')
const asISO = (v) => {
  const m = String(v || '').match(/(\d{4})-(\d{1,2})-(\d{1,2})/)
  return m ? `${m[1]}-${pad2(+m[2])}-${pad2(+m[3])}` : ''
}
const isoYMD = (dt) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
const vClass = (vt) => /10\s*t|13\s*t|大型|増ト/i.test(String(vt || '')) ? 'large' : 'medium'

// 車種文字列 → 試算の燃費・軽油単価・円/km（表示用。試算パネル外のUIから使う）。
export const quoteFuelRate = (vehicleClass) => fuelRateOf(vClass(vehicleClass))

function bizDaysBetween(loadStr, unloadStr) {
  const a = asISO(loadStr); if (!a) return []
  const b = asISO(unloadStr) || a
  const s = new Date(a + 'T00:00:00'), e = new Date(b + 'T00:00:00')
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return isBusinessDay(s) ? [a] : []
  const out = []; let c = new Date(s), g = 0
  while (c <= e && g < 60) { if (isBusinessDay(c)) out.push(isoYMD(c)); c.setDate(c.getDate() + 1); g++ }
  return out
}

// 着け日が日曜または祝日か。祝日データは operations.js の japanHolidays を再利用
// （isBusinessDay と同一ソース＝祝日リストを二重定義しない）。
const isSunOrHoliday = (iso) => {
  if (!iso) return false
  const dt = new Date(iso + 'T00:00:00')
  if (isNaN(dt.getTime())) return false
  return dt.getDay() === 0 || japanHolidays(dt.getFullYear()).has(iso)
}

export function quoteOwnTrip(input = {}) {
  const fare = Number(input.fare) || 0
  // 受注試算は「試算用」率（settings/labor.estimatePct・2026-08-27 一本化）。明示指定 > 試算用率 > LABOR_RATE(初期値35%)
  const laborRate = input.laborRate != null ? Number(input.laborRate) : (getLabor().rate ?? LABOR_RATE)
  const days = bizDaysBetween(input.date, input.unloadDate)
  const cls = vClass(input.vehicleClass)
  // 所要時間（Google経路・分）があれば時間ベース。無ければ null → 従来の運賃×%
  const laborTime = timeLaborCost({
    durationMin: input.durationMin, stopCount: input.stopCount, cls,
    cfg: getLabor().time, // settings/labor の時給・係数（未設定は既定。laborQuote.core が正規化）
    holidayArrive: isSunOrHoliday(asISO(input.unloadDate) || asISO(input.date)), // 着け日＝最後の卸地の日付（無ければ積日）
    night: isNightTime(input.loadTime) || isNightTime(input.unloadTime), // 積み or 着けが22:00〜翌5:00。時刻未入力は適用しない
  })
  const labor = laborTime ? laborTime.labor : Math.round(fare * laborRate)
  const dayRate = cls === 'large' ? VEHICLE_DAY_RATE.large : VEHICLE_DAY_RATE.medium
  const carCost = days.length ? days.length * dayRate : 0
  const km = Number(input.km) || 0
  // 燃料は車格別燃費（settings/assumptions・未設定/不正値は既定 中型4・大型2へ倒す）。明示指定 > 車格別
  const fr = fuelRateOf(cls)
  const yenPerKm = input.yenPerKm != null && input.yenPerKm !== '' ? Number(input.yenPerKm) : fr.yenPerKm
  const fuelKnown = km > 0
  const fuel = fuelKnown ? Math.round(km * yenPerKm) : 0
  const runCost = carCost + fuel
  const cost = labor + runCost
  // 時間モード: 人件費が運賃に依らない実額になったため、分岐＝経費合計・目安＝経費合計×15% に自然に乗る。
  // フォールバック（率）時は従来式のまま（挙動不変＝PR #53 までの表示を壊さない）。
  const suggestedFare = Math.round((laborTime ? cost : runCost) * (1 + QUOTE_MARGIN))
  const breakEven = laborTime ? cost : suggestedFare
  const contrib = fare - cost
  return {
    vClass: cls, // 'large'|'medium'（表示用。燃費・時給・車両日額の選択根拠）
    laborRate, labor, laborMode: laborTime ? 'time' : 'rate', laborTime: laborTime || null,
    days: days.length, carCost, fuel, fuelKnown, yenPerKm, kmPerL: fr.kmPerL, yenPerL: fr.yenPerL, runCost, cost,
    breakEven, suggestedFare, contrib, aboveBreakEven: fare >= breakEven,
  }
}
